import {Box, ThemeProvider} from '@primer/react-brand/esm'
import {WebGLPage} from '@github-ui/brand-webgl'
import {useMemo} from 'react'
import {
  buildPersonalizationValue,
  PersonalizationProvider,
  type PersonalizationUserStateProps,
} from '@github-ui/swp-core/contexts/PersonalizationContext'
import type {FlexSuiteCategoryPage} from '../types'
import {FlexSuiteHero} from '../components/FlexSuiteHero'
import {FlexSuiteSubNav} from '../components/FlexSuiteSubNav'
import {IndexSection} from './components/IndexSection'
import {IntroSection} from './components/IntroSection'
import {OutroSection} from './components/OutroSection'
import {BrandTreatmentProvider} from '../hooks/useBrandTreatment'
import templateVarsStyles from '../styles/variables.module.css'

export type CategoryTemplateProps = {
  page: FlexSuiteCategoryPage
} & PersonalizationUserStateProps

export function CategoryTemplate({
  page,
  userLoggedIn,
  userCopilotPlan,
  orgMembershipType,
  orgAdminType,
}: CategoryTemplateProps) {
  const personalizationValue = useMemo(
    () =>
      buildPersonalizationValue({
        userLoggedIn,
        userCopilotPlan,
        orgMembershipType,
        orgAdminType,
      }),
    [userLoggedIn, userCopilotPlan, orgMembershipType, orgAdminType],
  )

  return (
    <PersonalizationProvider value={personalizationValue}>
      <CategoryTemplateContent page={page} />
    </PersonalizationProvider>
  )
}

function CategoryTemplateContent({page}: Pick<CategoryTemplateProps, 'page'>) {
  const {template, settings: pageSettings} = page.fields
  const pageColorMode = pageSettings?.fields.colorMode ?? 'light'
  const {
    subNav,
    indexSections,
    settings: templateSettings,
    heroHeading,
    heroLabel,
    heroDescription,
    heroVariant,
    heroAsset,
    heroCtaOne,
    heroCtaTwo,
    heroImageCards,
    heroIconCards,
    introLogos,
    introLogoLink,
    introStats,
    sectionIntroHeading,
    sectionIntroLabel,
    sectionIntroDescription,
    sectionIntroLink,
    breakoutVariant,
    featureRiverBreakout,
    featureBreakoutTabs,
    cardsVariant,
    cardsIconItems,
    cardsImageItems,
    indexTestimonialVariant,
    indexTestimonialLink,
    indexTestimonialLogo,
    indexTestimonialQuote,
    indexTestimonialImage,
    indexTestimonialLabel,
    indexTestimonialName,
    indexTestimonialTitle,
    outroLargeCards,
    outroTestimonial,
    outroTestimonialLink,
    outroTestimonialLogo,
    outroTestimonialQuote,
    outroTestimonialImage,
    outroTestimonialLabel,
    outroTestimonialName,
    outroTestimonialTitle,
    outroStats,
    outroCustomerStories,
    outroCtaHeading,
    outroCtaDescription,
    outroCtaOne,
    outroCtaTwo,
    outroBrandedDivider,
    outroResourcesHeading,
    outroResources,
  } = template.fields

  return (
    <ThemeProvider colorMode={pageColorMode}>
      <BrandTreatmentProvider brand={templateSettings?.fields.brand}>
        {/* Keep background synchronized with the current color mode */}
        <Box backgroundColor="default" className={templateVarsStyles.templateVars}>
          <WebGLPage mode={pageColorMode}>
            {subNav && <FlexSuiteSubNav subNav={subNav} />}
            <FlexSuiteHero
              heroHeading={heroHeading}
              heroLabel={heroLabel}
              heroDescription={heroDescription}
              heroVariant={heroVariant}
              heroAsset={heroAsset}
              heroCtaOne={heroCtaOne}
              heroCtaOnePersonalisation={templateSettings?.fields.heroCtaOnePersonalisation}
              heroCtaTwo={heroCtaTwo}
              heroCtaTwoPersonalisation={templateSettings?.fields.heroCtaTwoPersonalisation}
              heroImageCards={heroImageCards}
              heroIconCards={heroIconCards}
              options={{
                variant: 'gridline',
                align: 'center',
                enableAnimation: true,
                textVariant: 'muted',
              }}
            />
            <IntroSection
              introLogos={introLogos}
              introLogoLink={introLogoLink}
              introStats={introStats}
              logobarOptions={{marquee: true, marqueeSpeed: 'slow', variant: 'muted'}}
              logoSuiteOptions={{variant: 'gridline-expressive'}}
            />
            {indexSections?.length ? (
              indexSections.map(section => <IndexSection key={section.sys.id} {...section.fields} />)
            ) : (
              <IndexSection
                sectionIntroHeading={sectionIntroHeading}
                sectionIntroLabel={sectionIntroLabel}
                sectionIntroDescription={sectionIntroDescription}
                sectionIntroLink={sectionIntroLink}
                breakoutVariant={breakoutVariant}
                featureRiverBreakout={featureRiverBreakout}
                featureBreakoutTabs={featureBreakoutTabs}
                cardsVariant={cardsVariant}
                cardsIconItems={cardsIconItems}
                cardsImageItems={cardsImageItems}
                indexTestimonialVariant={indexTestimonialVariant}
                indexTestimonialLink={indexTestimonialLink}
                indexTestimonialLogo={indexTestimonialLogo}
                indexTestimonialQuote={indexTestimonialQuote}
                indexTestimonialImage={indexTestimonialImage}
                indexTestimonialLabel={indexTestimonialLabel}
                indexTestimonialName={indexTestimonialName}
                indexTestimonialTitle={indexTestimonialTitle}
              />
            )}
            <OutroSection
              settings={templateSettings}
              outroLargeCards={outroLargeCards}
              outroTestimonial={outroTestimonial}
              outroTestimonialLink={outroTestimonialLink}
              outroTestimonialLogo={outroTestimonialLogo}
              outroTestimonialQuote={outroTestimonialQuote}
              outroTestimonialImage={outroTestimonialImage}
              outroTestimonialLabel={outroTestimonialLabel}
              outroTestimonialName={outroTestimonialName}
              outroTestimonialTitle={outroTestimonialTitle}
              outroStats={outroStats}
              outroCustomerStories={outroCustomerStories}
              outroCtaHeading={outroCtaHeading}
              outroCtaDescription={outroCtaDescription}
              outroCtaOne={outroCtaOne}
              outroCtaTwo={outroCtaTwo}
              outroBrandedDivider={outroBrandedDivider}
              outroResourcesHeading={outroResourcesHeading}
              outroResources={outroResources}
            />
          </WebGLPage>
        </Box>
      </BrandTreatmentProvider>
    </ThemeProvider>
  )
}
